// Compliance Master data-access module.
// Real backend integration for list / categories / create / update / delete / download.
// Entity Name / Entity TAN options are loaded from the shared Entity Master
// (getEntityList) and TAN Master (getTANList) APIs directly in the component.
//
// NOTE: The console.log statements below are temporary test aids to verify the
// exact payload/response going to/from the API. Remove them after testing.

import { GLOBAL_CONSTANTS } from '../constants/globalConstants'
import {
  BASE_URL,
  COMPLIANCE_MASTER_BASE_API,
  COMPLIANCE_MASTER_CATEGORIES_API,
  COMPLIANCE_MASTER_CREATE_API,
  COMPLIANCE_MASTER_DELETE_API,
  COMPLIANCE_MASTER_DOWNLOAD_API,
  COMPLIANCE_MASTER_LIST_API,
} from '../endpoints/apiEndpoints'
import { getAccessToken } from '../utils/session'

const parseError = async response => {
  let errorMessage
  try {
    const errorData = await response.json()
    errorMessage =
      errorData.message ||
      errorData.error ||
      `Request failed with status: ${response.status}`
  } catch {
    errorMessage = `Request failed with status: ${response.status}`
  }
  return errorMessage
}

// ---- Table list -----------------------------------------------------------
export const getComplianceEntryList = async payload => {
  try {
    const token = getAccessToken()
    const apiUrl = `${BASE_URL}/${COMPLIANCE_MASTER_LIST_API}`

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: token,
      },
      body: JSON.stringify(payload),
    })

    if (!response.ok) throw new Error(await parseError(response))

    const data = await response.json()

    return { success: true, data }
  } catch (error) {
    console.error('[ComplianceMaster] LIST error:', error)
    return {
      success: false,
      error: error.message || 'Failed to fetch Compliance Entry list',
    }
  }
}

// ---- Compliance categories (for the Add modal) ----------------------------
// Response shape: array of strings e.g. ['TDS Liability Computation', 'Form 26Q']
// Categories are country-scoped and the backend requires `countryCode`
// (500 when omitted) — the India screen defaults to India ('IND').
export const getComplianceCategoryList = async (
  countryCode = GLOBAL_CONSTANTS.INDIA_COUNTRY_CODE
) => {
  try {
    const token = getAccessToken()
    const query = new URLSearchParams({
      countryCode:
        countryCode || GLOBAL_CONSTANTS.INDIA_COUNTRY_CODE,
    }).toString()
    const apiUrl = `${BASE_URL}/${COMPLIANCE_MASTER_CATEGORIES_API}?${query}`

    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: token,
      },
    })

    if (!response.ok) throw new Error(await parseError(response))

    const data = await response.json()

    return { success: true, data }
  } catch (error) {
    console.error('[ComplianceMaster] CATEGORIES error:', error)
    return {
      success: false,
      error: error.message || 'Failed to fetch Compliance Category list',
    }
  }
}

// ---- Create ---------------------------------------------------------------
// payload: { countryCode: 'IND', complianceCategories: string[], entityUuids: string[],
//            tanUuids: string[], startDate: 'YYYY-MM-DD' }
export const createComplianceEntry = async payload => {
  try {
    const token = getAccessToken()
    const apiUrl = `${BASE_URL}/${COMPLIANCE_MASTER_CREATE_API}`

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: token,
      },
      body: JSON.stringify(payload),
    })

    if (!response.ok) throw new Error(await parseError(response))

    const data = await response.json().catch(() => ({}))

    return { success: true, data }
  } catch (error) {
    console.error('[ComplianceMaster] CREATE error:', error)
    return {
      success: false,
      error: error.message || 'Failed to create Compliance Entry',
    }
  }
}

// ---- Update completion date -----------------------------------------------
// PATCH `${BASE}/compliance-master/{uuid}/completion-date` body { completionDate }
export const updateComplianceEntry = async (uuid, updates) => {
  try {
    const token = getAccessToken()
    const apiUrl = `${BASE_URL}/${COMPLIANCE_MASTER_BASE_API}/${uuid}/completion-date`
    const body = { completionDate: updates.completionDate }

    const response = await fetch(apiUrl, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: token,
      },
      body: JSON.stringify(body),
    })

    if (!response.ok) throw new Error(await parseError(response))

    const data = await response.json().catch(() => ({}))

    return { success: true, data }
  } catch (error) {
    console.error('[ComplianceMaster] UPDATE error:', error)
    return {
      success: false,
      error: error.message || 'Failed to update Compliance Entry',
    }
  }
}

// ---- Delete ---------------------------------------------------------------
// Sends an array of uuids in the request body.
export const deleteComplianceEntries = async uuids => {
  try {
    const token = getAccessToken()
    const apiUrl = `${BASE_URL}/${COMPLIANCE_MASTER_DELETE_API}`

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: token,
      },
      body: JSON.stringify(uuids),
    })

    if (!response.ok) throw new Error(await parseError(response))

    const data = await response.json().catch(() => ({}))

    return { success: true, data }
  } catch (error) {
    console.error('[ComplianceMaster] DELETE error:', error)
    return {
      success: false,
      error: error.message || 'Failed to delete Compliance Entries',
    }
  }
}

// Entity Name / Entity TAN options are loaded from the shared Entity Master
// (getEntityList) and TAN Master (getTANList) APIs directly in the component,
// mirroring the Assign Entity form. No dedicated lookup endpoint is needed here.

// ---- Download (report request) --------------------------------------------
// This does NOT return a file directly. It queues a report; the backend responds
// with JSON (including a download id) and the generated file is retrieved later
// from the Download page. Mirrors the Returns Excel download flow.
export const downloadComplianceExcel = async payload => {
  try {
    const token = getAccessToken()
    const apiUrl = `${BASE_URL}/${COMPLIANCE_MASTER_DOWNLOAD_API}`

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: token,
      },
      body: JSON.stringify(payload),
    })

    if (!response.ok) throw new Error(await parseError(response))

    const data = await response.blob().catch(() => ({}))

    return { success: true, data }
  } catch (error) {
    console.error('[ComplianceMaster] DOWNLOAD error:', error)
    return {
      success: false,
      error: error.message || 'Failed to initiate Compliance download',
    }
  }
}
